import { Box, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import type { ProductDetail } from '../../api/business/types';
import { LanguageTabs } from '../../components/LanguageTabs';
import {
  META_DESCRIPTION_ADVISED,
  META_DESCRIPTION_MAX,
  META_TITLE_ADVISED,
  META_TITLE_MAX,
} from '../../components/productDraft';
import { lengthState } from '../../components/productListing';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

type ProductSeoSectionProps = {
  product: ProductDetail;
};

/**
 * What search engines show of a product, per content language (R0030 WP-07): its title and description with their
 * length — or, where none is written, what the shops use instead.
 */
export const ProductSeoSection = ({ product }: ProductSeoSectionProps) => {
  const { t } = useLanguage();
  const { languages } = useContentLanguages();
  const [active, setActive] = useState(languages[0] ?? 'sk');
  const current = languages.includes(active) ? active : languages[0];
  const texts = product.translations[current];
  const count = (text: string, advised: number, max: number) => (
    <Typography
      variant="caption"
      color={lengthState(text.length, advised, max) === 'OK' ? 'text.secondary' : 'warning.main'}
    >
      {lengthState(text.length, advised, max) === 'OK'
        ? t('seo.count', { count: text.length, max })
        : t('seo.countLong', { count: text.length, max, advised })}
    </Typography>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t('seo.title')}
      </Typography>
      {languages.length > 1 ? (
        <LanguageTabs
          languages={languages}
          value={current}
          onChange={setActive}
          missing={(code) => !product.translations[code]?.metaTitle}
        />
      ) : null}
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('seo.metaTitle')}
          </Typography>
          {texts?.metaTitle ? (
            <>
              <Typography variant="body2">{texts.metaTitle}</Typography>
              {count(texts.metaTitle, META_TITLE_ADVISED, META_TITLE_MAX)}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('seo.usesName', { name: texts?.name ?? product.name })}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('seo.metaDescription')}
          </Typography>
          {texts?.metaDescription ? (
            <>
              <Typography variant="body2">{texts.metaDescription}</Typography>
              {count(texts.metaDescription, META_DESCRIPTION_ADVISED, META_DESCRIPTION_MAX)}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('seo.usesShortDescription')}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};
